/**
 * KaTeX typesetting for server-rendered math placeholders (US-02 / FR-3.1).
 *
 * The markdown renderer (packages/vault-parsers) emits
 * `<span class="math math-inline">` / `<div class="math math-display">`
 * whose text content is the HTML-escaped TeX source. This module upgrades
 * those placeholders in the browser: `texToHtml` produces KaTeX markup for
 * one source string, and `renderMathIn` walks a mounted DOM subtree and
 * replaces every un-typeset `.math` element's content in place.
 *
 * Failure policy: KaTeX runs with `throwOnError: false`, so malformed TeX
 * renders as KaTeX's own red error text; if KaTeX throws anyway the
 * placeholder keeps its escaped literal source — never raw HTML.
 */

import katex from 'katex';
import 'katex/dist/katex.min.css';

/** Render one TeX source string to KaTeX HTML markup. */
export function texToHtml(tex: string, displayMode: boolean): string {
  return katex.renderToString(tex, {
    displayMode,
    throwOnError: false,
    output: 'htmlAndMathml',
  });
}

/**
 * Typeset every `.math` placeholder under `root` that has not been
 * typeset yet. Idempotent — safe to call on every render/effect pass.
 */
export function renderMathIn(root: ParentNode): void {
  const nodes = root.querySelectorAll<HTMLElement>('.math:not([data-math-rendered])');
  for (const el of Array.from(nodes)) {
    const tex = el.textContent ?? '';
    try {
      el.innerHTML = texToHtml(tex, el.classList.contains('math-display'));
      el.setAttribute('data-math-rendered', 'true');
    } catch {
      // Keep the escaped literal TeX source visible.
      el.setAttribute('data-math-rendered', 'error');
    }
  }
}
